import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { Users, Plus, Search, Dog, Calendar, Phone, Mail, Edit, Edit2, Trash2, RefreshCw, GitMerge, Activity, Hash } from 'lucide-react';
import { Link } from 'react-router-dom';
import { admin as adminApi, membershipIds as membershipIdsApi } from '../utils/api';
import CreateClientModal from '../components/CreateClientModal';
import AddPetModal from '../components/AddPetModal';
import EditClientModal from '../components/EditClientModal';
import BookForClientModal from '../components/BookForClientModal';
import MergeUsersModal from '../components/MergeUsersModal';
import ClientDocumentsPanel from '../components/ClientDocumentsPanel';
import GuestsPanel from '../components/GuestsPanel';
import useSubjectSettings from '../hooks/useSubjectSettings';
import { useAuth } from '../context/AuthContext';
import { parseLocalDate } from '../utils/timeFormat';

const ClientManagement = () => {
  const { isEnabled: subjectsEnabled, pluralName: subjectPluralName, singularName: subjectSingularName } = useSubjectSettings();
  const { user: authUser } = useAuth();
  const viewerRole = authUser?.role || 'admin';
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [showAddPet, setShowAddPet] = useState(false);
  const [showEditClient, setShowEditClient] = useState(false);
  const [showBookAppointment, setShowBookAppointment] = useState(false);
  const [showMergeClients, setShowMergeClients] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientDetails, setSelectedClientDetails] = useState(null);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue, setNotesValue] = useState('');

  const filterClients = useCallback(() => {
    if (!searchTerm.trim()) {
      setFilteredClients(clients);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = clients.filter(client => 
      client.first_name?.toLowerCase().includes(term) ||
      client.last_name?.toLowerCase().includes(term) ||
      client.email?.toLowerCase().includes(term) ||
      client.phone?.includes(term) ||
      client.membership_id?.toLowerCase().includes(term)
    );
    setFilteredClients(filtered);
  }, [searchTerm, clients]);

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    filterClients();
  }, [searchTerm, clients, filterClients]);

  const loadClients = async () => {
    try {
      const response = await adminApi.getClients();
      setClients(response.data);
      setFilteredClients(response.data);
    } catch (error) {
      console.error('Load clients error:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const loadClientDetails = async (clientId) => {
    try {
      const response = await adminApi.getClientDetails(clientId);
      setSelectedClientDetails(response.data);
    } catch (error) {
      console.error('Load client details error:', error);
      toast.error('Failed to load client details');
    }
  };

  const handleAddPet = (client) => {
    setSelectedClient(client);
    loadClientDetails(client.id);
    setShowAddPet(true);
  };

  const handleBookAppointment = (client) => {
    setSelectedClient(client);
    loadClientDetails(client.id);
    setShowBookAppointment(true);
  };

  const handleViewDetails = (client) => {
    setSelectedClient(client);
    loadClientDetails(client.id);
  };

  const handleEditNotes = () => {
    setNotesValue(selectedClientDetails?.notes || '');
    setEditingNotes(true);
  };

  const handleSaveNotes = async () => {
    try {
      await adminApi.updateClient(selectedClient.id, {
        first_name: selectedClient.first_name,
        last_name: selectedClient.last_name,
        email: selectedClient.email,
        phone: selectedClient.phone,
        notes: notesValue || null,
      });
      setSelectedClientDetails(prev => ({ ...prev, notes: notesValue || null }));
      setEditingNotes(false);
      toast.success('Notes updated');
    } catch (error) {
      console.error('Save notes error:', error);
      toast.error('Failed to update notes');
    }
  };

  const handleClearNotes = async () => {
    if (!window.confirm('Are you sure you want to delete all account notes?')) return;
    try {
      await adminApi.updateClient(selectedClient.id, {
        first_name: selectedClient.first_name,
        last_name: selectedClient.last_name,
        email: selectedClient.email,
        phone: selectedClient.phone,
        notes: null,
      });
      setSelectedClientDetails(prev => ({ ...prev, notes: null }));
      setEditingNotes(false);
      toast.success('Notes cleared');
    } catch (error) {
      console.error('Clear notes error:', error);
      toast.error('Failed to clear notes');
    }
  };

  // Roadmap item #21: assign / regenerate / clear membership ID
  // inline from the client list. Errors surface via toast and the
  // local client row is patched in-place so the UI updates without a
  // full reload.
  const patchClientLocal = (clientId, patch) => {
    setClients((prev) => prev.map((c) => (c.id === clientId ? { ...c, ...patch } : c)));
    setFilteredClients((prev) => prev.map((c) => (c.id === clientId ? { ...c, ...patch } : c)));
  };

  const handleAssignMembershipId = async (client, { regenerate = false } = {}) => {
    if (regenerate && !window.confirm(`Regenerate membership ID for ${client.first_name} ${client.last_name}? The current ID will be replaced.`)) return;
    try {
      const res = await membershipIdsApi.assign(client.id, { regenerate });
      const newId = res.data?.membership_id;
      patchClientLocal(client.id, { membership_id: newId });
      toast.success(regenerate ? 'Membership ID regenerated' : 'Membership ID assigned');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to assign membership ID');
    }
  };

  const handleClearMembershipId = async (client) => {
    if (!window.confirm(`Remove membership ID ${client.membership_id} from ${client.first_name} ${client.last_name}?`)) return;
    try {
      await membershipIdsApi.clear(client.id);
      patchClientLocal(client.id, { membership_id: null });
      toast.success('Membership ID cleared');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to clear membership ID');
    }
  };

  const handleDeleteClient = async (client) => {
    if (!window.confirm(`Are you sure you want to delete ${client.first_name} ${client.last_name}? This will also delete all their pets and appointments.`)) {
      return;
    }
    
    try {
      await adminApi.deleteUser(client.id);
      toast.success('Client deleted successfully');
      loadClients();
      if (selectedClient?.id === client.id) {
        setSelectedClient(null);
        setSelectedClientDetails(null);
      }
    } catch (error) {
      console.error('Delete client error:', error);
      toast.error('Failed to delete client');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Users className="text-primary-600" size={32} />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Client Management</h1>
              <p className="text-gray-600 text-sm mt-1">
                {subjectsEnabled 
                  ? `Create profiles, add ${subjectPluralName.toLowerCase()}, and book appointments for clients`
                  : 'Create profiles and book appointments for clients'
                }
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadClients}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
              title="Refresh client list"
            >
              <RefreshCw size={18} />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => setShowMergeClients(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              title="Merge duplicate client accounts"
            >
              <GitMerge size={18} />
              <span>Merge Clients</span>
            </button>
            <button
              onClick={() => setShowCreateClient(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Plus size={20} />
              <span>New Client</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="card mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="card bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Clients</p>
                <p className="text-3xl font-bold mt-1">{clients.length}</p>
              </div>
              <Users size={40} className="text-blue-200" />
            </div>
          </div>

          <div className="card bg-gradient-to-br from-green-500 to-green-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Active Clients</p>
                <p className="text-3xl font-bold mt-1">
                  {clients.filter(c => c.total_appointments > 0).length}
                </p>
              </div>
              <Calendar size={40} className="text-green-200" />
            </div>
          </div>

          {subjectsEnabled && (
            <div className="card bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Total {subjectPluralName}</p>
                  <p className="text-3xl font-bold mt-1">
                    {clients.reduce((sum, c) => sum + (parseInt(c.total_pets) || 0), 0)}
                  </p>
                </div>
                <Dog size={40} className="text-purple-200" />
              </div>
            </div>
          )}
        </div>

        {/* Roadmap item #20 Phase 2: guest accounts panel. Renders
            nothing when there are no guests, so non-users of the
            feature don't see clutter. */}
        {viewerRole === 'admin' && <GuestsPanel />}

        {/* Clients List */}
        <div className="space-y-4">
          {filteredClients.length === 0 ? (
            <div className="card text-center py-12">
              <Users className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600 text-lg">
                {searchTerm ? 'No clients found matching your search' : 'No clients yet'}
              </p>
              <button
                onClick={() => setShowCreateClient(true)}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Create First Client
              </button>
            </div>
          ) : (
            filteredClients.map((client) => (
              <div key={client.id} className="card hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {/* Client Name */}
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {client.first_name} {client.last_name}
                    </h3>

                    {/* Contact Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                      <div className="flex items-center space-x-2 text-gray-600">
                        <Mail size={16} />
                        <span className="text-sm">{client.email}</span>
                      </div>
                      {client.phone && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Phone size={16} />
                          <span className="text-sm">{client.phone}</span>
                        </div>
                      )}
                      {/* Roadmap item #21: membership ID + admin
                          actions. Visible to admins regardless of
                          assignment state so they can issue/clear on
                          demand; clients without an ID see a small
                          "Assign" link only when expanded. */}
                      {viewerRole === 'admin' && (
                        <div className="flex items-center space-x-2 text-gray-600">
                          <Hash size={16} />
                          {client.membership_id ? (
                            <>
                              <span className="text-sm font-mono">{client.membership_id}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleAssignMembershipId(client, { regenerate: true }); }}
                                className="text-xs text-primary-600 hover:underline"
                                title="Regenerate"
                              >
                                regen
                              </button>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleClearMembershipId(client); }}
                                className="text-xs text-red-600 hover:underline"
                                title="Clear"
                              >
                                clear
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleAssignMembershipId(client); }}
                              className="text-sm text-primary-600 hover:underline"
                            >
                              Assign membership ID
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Stats */}
                    <div className="flex items-center space-x-4 text-sm">
                      {subjectsEnabled && (
                        <div className="flex items-center space-x-1 text-gray-600">
                          <Dog size={16} />
                          <span>{client.total_pets || 0} {subjectPluralName.toLowerCase()}</span>
                        </div>
                      )}
                      <div className="flex items-center space-x-1 text-gray-600">
                        <Calendar size={16} />
                        <span>{client.total_appointments || 0} appointment{client.total_appointments !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col space-y-2 ml-4">
                    <button
                      onClick={() => handleBookAppointment(client)}
                      className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                    >
                      <Calendar size={16} />
                      <span>Book Appointment</span>
                    </button>
                    {subjectsEnabled && (
                      <button
                        onClick={() => handleAddPet(client)}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        <Dog size={16} />
                        <span>Add {subjectSingularName}</span>
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setSelectedClient(client);
                        loadClientDetails(client.id).then(() => setShowEditClient(true));
                      }}
                      className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm"
                    >
                      <Edit2 size={16} />
                      <span>Edit Info</span>
                    </button>
                    <button
                      onClick={() => handleViewDetails(client)}
                      className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                    >
                      <Edit size={16} />
                      <span>View Details</span>
                    </button>
                    <button
                      onClick={() => handleDeleteClient(client)}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      <Trash2 size={16} />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>

                {/* Client Details (when selected) */}
                {selectedClient?.id === client.id && selectedClientDetails && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    {/* Notes Section (if exists) */}
                    {(selectedClientDetails.notes || editingNotes) && (
                      <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">Account Notes:</h4>
                          {!editingNotes && (
                            <div className="flex gap-2">
                              <button
                                onClick={handleEditNotes}
                                className="text-xs px-2 py-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="Edit notes"
                              >
                                <Edit2 size={14} className="inline mr-1" />Edit
                              </button>
                              <button
                                onClick={handleClearNotes}
                                className="text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete notes"
                              >
                                <Trash2 size={14} className="inline mr-1" />Delete
                              </button>
                            </div>
                          )}
                        </div>
                        {editingNotes ? (
                          <div>
                            <textarea
                              value={notesValue}
                              onChange={(e) => setNotesValue(e.target.value)}
                              rows={6}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm font-sans resize-y"
                            />
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={handleSaveNotes}
                                className="px-3 py-1 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setEditingNotes(false)}
                                className="px-3 py-1 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">
                            {selectedClientDetails.notes}
                          </pre>
                        )}
                      </div>
                    )}

                    {subjectsEnabled && (
                      <>
                        <h4 className="font-semibold text-gray-900 mb-3">{subjectPluralName}:</h4>
                        {selectedClientDetails.pets?.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {selectedClientDetails.pets.map(pet => (
                              <div key={pet.id} className="p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center space-x-2">
                                    <Dog size={16} className="text-primary-600" />
                                    <span className="font-semibold">{pet.name}</span>
                                  </div>
                                  <Link
                                    to={`/pets/${pet.id}/chart`}
                                    className="inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-100 rounded transition-colors"
                                    title="Open full chart"
                                  >
                                    <Activity size={12} />
                                    <span>Chart</span>
                                  </Link>
                                </div>
                                <p className="text-sm text-gray-600">
                                  {pet.breed} • {pet.size} • {pet.age} years old
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">No {subjectPluralName.toLowerCase()} added yet</p>
                        )}
                      </>
                    )}

                    <h4 className="font-semibold text-gray-900 mb-3 mt-4">Recent Appointments:</h4>
                    {selectedClientDetails.appointments?.length > 0 ? (
                      <div className="space-y-2">
                        {selectedClientDetails.appointments.slice(0, 3).map(apt => (
                          <div key={apt.id} className="p-3 bg-gray-50 rounded-lg text-sm">
                            <div className="flex items-center justify-between">
                              <span className="font-medium">{apt.service_name || 'Standard Appointment'}</span>
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                apt.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                apt.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {apt.status}
                              </span>
                            </div>
                            <p className="text-gray-600 mt-1">
                              {parseLocalDate(apt.appointment_date).toLocaleDateString()} at {apt.start_time}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">No appointments yet</p>
                    )}

                    {/* Documents & Requests */}
                    <div className="mt-6 pt-4 border-t border-gray-200">
                      <ClientDocumentsPanel
                        clientId={client.id}
                        viewerRole={viewerRole}
                        clientName={`${client.first_name || ''} ${client.last_name || ''}`.trim()}
                      />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modals */}
      {showCreateClient && (
        <CreateClientModal
          onClose={() => setShowCreateClient(false)}
          onSuccess={() => {
            setShowCreateClient(false);
            loadClients();
          }}
        />
      )}

      {showAddPet && selectedClient && (
        <AddPetModal
          client={selectedClient}
          onClose={() => {
            setShowAddPet(false);
            setSelectedClient(null);
          }}
          onSuccess={() => {
            setShowAddPet(false);
            loadClients();
            if (selectedClient) {
              loadClientDetails(selectedClient.id);
            }
          }}
        />
      )}

      {showEditClient && selectedClient && selectedClientDetails && (
        <EditClientModal
          client={selectedClientDetails}
          onClose={() => {
            setShowEditClient(false);
          }}
          onSuccess={() => {
            setShowEditClient(false);
            loadClients();
            if (selectedClient) {
              loadClientDetails(selectedClient.id);
            }
          }}
        />
      )}

      {showBookAppointment && selectedClient && selectedClientDetails && (
        <BookForClientModal
          client={selectedClient}
          clientDetails={selectedClientDetails}
          onClose={() => {
            setShowBookAppointment(false);
            setSelectedClient(null);
          }}
          onSuccess={() => {
            setShowBookAppointment(false);
            loadClients();
          }}
        />
      )}

      {showMergeClients && (
        <MergeUsersModal
          allUsers={clients.map(c => ({ ...c, role: 'client' }))}
          onClose={() => setShowMergeClients(false)}
          onSuccess={() => {
            setShowMergeClients(false);
            loadClients();
          }}
        />
      )}
    </div>
  );
};

export default ClientManagement;
